import { Box, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CardShell } from '../../atlas/cards/CardShell';
import { MetricRow } from '../../atlas/cards/MetricRow';
import { CHART_GOAL_LINE_COLOR, CHART_GOAL_LINE_PROPS } from '../../constants';
import { aggregateQuarterlyChartToAnnual } from '../../chartGoalUtils';
import { buildCountYAxisScaleForChart } from '../../chartUtils';
import { countYAxisReferenceGridLines } from '../../countYAxisReferenceGridLines';
import { latest, parseQuarter, pctChange } from '../../metricSeriesUtils';
import { roundedStackBarTopShape, stackBarTopShape, type BarShapeProps } from '../../rechartsStackedBarShapes';
import {
  buildMultiAnnualGoalStatusTooltip,
  getAnnualPaceStatus,
} from '../../statusUtils';
import {
  ARCH_CERTIFICATIONS_ANNUAL_GOAL,
  hasArchCertifiedSubawardeeBreakdown,
} from '../mapArchMetricsData';
import type { ArchMetricsData } from '../types';

const CERTIFIED_SUBAWARDEE_COLORS = ['#7048E8', '#20C997', '#FF8787', '#FFA94D'];
const CERTIFIED_COLOR = '#51CF66';

type CompanyCertificationCardProps = {
  data: ArchMetricsData;
  /** When false, hides the On Track / Snapshot status pill (admin component-project view). */
  showStatus?: boolean;
};

function formatYoY(yoy: number | null): string {
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  return yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;
}

function sumPriorYearYtd(
  series: ArchMetricsData['series'],
  currentMeta: { year: number; quarter: number } | null,
  getValue: (point: ArchMetricsData['series'][number]) => number
): number {
  if (!currentMeta) return 0;
  return series
    .filter((point) => {
      const meta = parseQuarter(point.quarter);
      return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
    })
    .reduce((sum, point) => sum + getValue(point), 0);
}

function getActiveCertifiedStackKeys(data: ArchMetricsData): string[] {
  if (!hasArchCertifiedSubawardeeBreakdown(data)) {
    return [];
  }

  const active = new Set<string>();
  for (const point of data.series) {
    for (const [subawardeeId, value] of Object.entries(point.companiesCertifiedBySubawardee ?? {})) {
      if (value > 0) active.add(subawardeeId);
    }
  }

  if (active.size > 0) {
    return data.certifiedSubawardeeIds.filter((subawardeeId) => active.has(subawardeeId));
  }

  return data.certifiedSubawardeeIds;
}

export function CompanyCertificationCard({
  data,
  showStatus = true,
}: CompanyCertificationCardProps) {
  const theme = useMantineTheme();
  const series = data.series;
  const cur = latest(series);
  const subtitle = cur?.quarter ?? 'No data';
  const isStackedBySubawardee = hasArchCertifiedSubawardeeBreakdown(data);
  const stackKeys = getActiveCertifiedStackKeys(data);

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytdCertified = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.companiesCertified, 0)
    : 0;

  const priorYearYtdCertified = sumPriorYearYtd(series, currentMeta, (point) => point.companiesCertified);
  const certifiedYoY = pctChange(ytdCertified, priorYearYtdCertified || null);

  const certificationsAnnualGoal =
    cur?.goalTargets?.companiesCertified ?? ARCH_CERTIFICATIONS_ANNUAL_GOAL;

  const status = showStatus
    ? !cur
      ? { label: 'Snapshot' as const, color: 'gray' as const, tooltip: undefined as string | undefined }
      : getAnnualPaceStatus({
          ytd: ytdCertified,
          goalPerYear: certificationsAnnualGoal,
          quarterLabel: cur.quarter,
          direction: 'min',
        })
    : null;

  const statusTooltip =
    !status || status.label === 'On track' || status.label === 'Snapshot'
      ? undefined
      : buildMultiAnnualGoalStatusTooltip({
          quarterLabel: cur?.quarter ?? '',
          summaryTooltip:
            status.label === 'Achieved' ? 'Annual certification goal achieved.' : undefined,
          metrics: [
            { label: 'Certified', ytd: ytdCertified, goalPerYear: certificationsAnnualGoal },
          ],
        });

  const chartData = isStackedBySubawardee
    ? (() => {
        const byYear = new Map<number, Record<string, number | string>>();

        for (const point of series) {
          const meta = parseQuarter(point.quarter);
          if (!meta) continue;

          const row = byYear.get(meta.year) ?? {
            year: meta.year,
            yearLabel: String(meta.year),
          };

          for (const subawardeeId of stackKeys) {
            row[subawardeeId] =
              Number(row[subawardeeId] ?? 0) +
              (point.companiesCertifiedBySubawardee?.[subawardeeId] ?? 0);
          }

          byYear.set(meta.year, row);
        }

        return Array.from(byYear.values()).sort((a, b) => Number(a.year) - Number(b.year));
      })()
    : aggregateQuarterlyChartToAnnual(
        series.map((point) => ({
          quarter: point.quarter,
          companiesCertified: point.companiesCertified,
        })),
        ['companiesCertified']
      );

  const chartMax = isStackedBySubawardee
    ? Math.max(
        0,
        ...chartData.flatMap((row) => stackKeys.map((subawardeeId) => Number(row[subawardeeId] ?? 0)))
      )
    : Math.max(0, ...chartData.map((point) => Number(point.companiesCertified)));

  const chartGoalValues = [certificationsAnnualGoal];
  const { yAxisMax, yAxisTicks } = buildCountYAxisScaleForChart([chartMax], chartGoalValues);
  const gridLineStroke =
    theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)';

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  return (
    <CardShell
      title="GreenUAS Certification"
      subtitle={subtitle}
      statusLabel={status?.label}
      statusColor={status?.color}
      statusTooltip={statusTooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'Certified (YTD)',
              labelTooltip: `Companies certified through the GreenUAS collaboration calendar year to date. Grant target: ${certificationsAnnualGoal} certifications per year.`,
              value: (
                <Text size="lg" weight={800}>
                  {ytdCertified}
                </Text>
              ),
              sublabel: (
                <>
                  {formatYoY(certifiedYoY)}
                  <br />
                  {`Goal ${certificationsAnnualGoal}/year`}
                </>
              ),
              reserveGoalSublabel: false,
            },
            {
              label: 'Certified (All-Time)',
              labelTooltip:
                'Total companies certified through GreenUAS across all reported quarters (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {data.allTime.companiesCertified}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          {isStackedBySubawardee
            ? 'Companies Certified by Sub-awardee'
            : 'Companies Certified by Year'}
        </Text>

        <Box sx={{ height: 180, minHeight: 180, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={180}>
            <BarChart data={chartData} barCategoryGap="12%">
              <CartesianGrid strokeDasharray="3 3" opacity={0.12} vertical={false} />
              <XAxis
                dataKey="yearLabel"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, yAxisMax]}
                ticks={yAxisTicks}
              />
              {countYAxisReferenceGridLines(yAxisTicks, gridLineStroke, chartGoalValues)}
              <ReferenceLine
                y={certificationsAnnualGoal}
                stroke={CHART_GOAL_LINE_COLOR}
                strokeWidth={CHART_GOAL_LINE_PROPS.strokeWidth}
                strokeDasharray={CHART_GOAL_LINE_PROPS.strokeDasharray}
                ifOverflow="extendDomain"
              />
              <Tooltip
                cursor={false}
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;

                  if (isStackedBySubawardee) {
                    const certifiedTotal = stackKeys.reduce((sum, subawardeeId) => {
                      const entry = payload.find((item) => item.dataKey === subawardeeId);
                      return sum + Number(entry?.value ?? 0);
                    }, 0);

                    return (
                      <div style={tooltipShellStyle}>
                        <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                          {label}
                        </div>
                        {stackKeys.map((subawardeeId) => {
                          const entry = payload.find((item) => item.dataKey === subawardeeId);
                          const value = Number(entry?.value ?? 0);
                          if (value <= 0) return null;
                          return (
                            <div
                              key={subawardeeId}
                              style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}
                            >
                              {subawardeeId}: {value}
                            </div>
                          );
                        })}
                        <div style={{ fontWeight: 700, fontSize: 13, marginTop: 6 }}>
                          Total: {certifiedTotal}
                        </div>
                      </div>
                    );
                  }

                  const certified = Number(
                    payload.find((entry) => entry.dataKey === 'companiesCertified')?.value ?? 0
                  );

                  return (
                    <div style={tooltipShellStyle}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                        {label}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}>
                        Companies Certified: {certified}
                      </div>
                    </div>
                  );
                }}
              />
              {isStackedBySubawardee ? (
                stackKeys.map((subawardeeId, index) => (
                  <Bar
                    key={subawardeeId}
                    dataKey={subawardeeId}
                    name={subawardeeId}
                    stackId="certified"
                    fill={CERTIFIED_SUBAWARDEE_COLORS[index % CERTIFIED_SUBAWARDEE_COLORS.length]}
                    shape={stackBarTopShape(stackKeys, subawardeeId)}
                    barSize={18}
                    isAnimationActive={false}
                    activeBar={false}
                  />
                ))
              ) : (
                <Bar
                  dataKey="companiesCertified"
                  name="Companies Certified"
                  fill={CERTIFIED_COLOR}
                  shape={(props: BarShapeProps) => roundedStackBarTopShape(props, true)}
                  barSize={18}
                  isAnimationActive={false}
                  activeBar={false}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {isStackedBySubawardee ? (
          <Group position="center" spacing="lg" mt={4}>
            {stackKeys.map((subawardeeId, index) => (
              <Group key={subawardeeId} spacing={6} noWrap>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor:
                      CERTIFIED_SUBAWARDEE_COLORS[index % CERTIFIED_SUBAWARDEE_COLORS.length],
                    flexShrink: 0,
                  }}
                />
                <Text size="xs" color="dimmed">
                  {subawardeeId}
                </Text>
              </Group>
            ))}
          </Group>
        ) : null}
      </Stack>
    </CardShell>
  );
}
